import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, PrinterIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useMyDocumentDetailQuery } from "../../api/queries";
import { DocumentPdfPreviewContent } from "../../ui/DocumentPdfPreviewContent";

const documentTypeLabel: Record<string, string> = {
  quotation: "Quotation",
  invoice: "Invoice",
  receipt: "Receipt",
};

export function MyDocumentPdfPreviewPage() {
  const { id } = useParams({ from: "/app/my-documents/$id/pdf-preview" });
  const nav = useNavigate({ from: "/app/my-documents/$id/pdf-preview" });
  const search = useSearch({ from: "/app/my-documents/$id/pdf-preview" });

  const { data, isLoading, error } = useMyDocumentDetailQuery(id ?? "");
  const hasTriggeredPrint = useRef(false);

  useEffect(() => {
    if (data?.document && id) {
      const docNumber =
        data.document.documentNumber ?? data.document.id.slice(0, 8);
      const typeLabel =
        documentTypeLabel[data.document.documentType] ?? "Document";
      document.title = `${typeLabel}-${docNumber}`;
    }
    return () => {
      document.title = "freedom® Admin";
    };
  }, [data?.document, id]);

  useEffect(() => {
    if (!data || !search.print || hasTriggeredPrint.current) return;
    hasTriggeredPrint.current = true;
    window.print();
    nav({
      to: "/app/my-documents/$id/pdf-preview",
      params: { id: id ?? "" },
      search: { print: undefined },
    });
  }, [data, search.print, id, nav]);

  const handleBack = () => {
    nav({ to: "/app/my-documents/$id", params: { id: id ?? "" } });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບເອກະສານ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ບໍ່ມີສິດເຂົ້າເຖິງ ຫຼື ເອກະສານຖືກລຶບແລ້ວ
              </p>
              <Button
                onClick={() =>
                  nav({
                    to: "/app/my-documents",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined,
                      filters: undefined,
                    },
                  })
                }
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປລາຍການ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header className="print:hidden" />

      <Main className="print:px-0 print:py-0">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບ
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
          </div>
        </div>

        <div>
          <DocumentPdfPreviewContent
            document={data.document}
            items={data.items}
            payments={data.payments}
          />
        </div>
      </Main>
    </>
  );
}
